import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Textarea } from '../../components/atoms/Textarea'
import { Paginator } from '../../components/molecules/Paginator'
import { useAccountTestimony } from '../../../viewmodels/account/useAccountTestimony'
import { Stagger, StaggerItem } from '../../motion'

const BODY_MIN = 10
const BODY_MAX = 2000

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/** `/account/testimony` — submit free-text testimonials about the
 *  brand (option (a) per Kiran 2026-06-15). Customer can submit
 *  multiple over time, and soft-delete their own. */
export function AccountTestimonyPage(): JSX.Element {
  const vm = useAccountTestimony()
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const trimmed = body.trim()
  const canSubmit = trimmed.length >= BODY_MIN && trimmed.length <= BODY_MAX && !submitting

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    setSuccess(null)
    if (!canSubmit) {
      setError(`Testimony must be between ${BODY_MIN} and ${BODY_MAX} characters`)
      return
    }
    setSubmitting(true)
    const res = await vm.submit(trimmed)
    setSubmitting(false)
    if (res.ok) {
      setBody('')
      setSuccess('Thanks — your testimony is saved.')
    } else {
      setError(res.message)
    }
  }

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Delete this testimony?')) return
    setError(null)
    setSuccess(null)
    setPendingDeleteId(id)
    const res = await vm.remove(id)
    setPendingDeleteId(null)
    if (!res.ok) setError(res.message)
  }

  return (
    <>
      <Helmet>
        <title>Testimony — Uyyamadilu</title>
      </Helmet>
      <h1 className="font-heading text-h1 text-charcoal mb-2">Testimony</h1>
      <p className="font-body text-body text-text-secondary mb-6">
        Share your experience with Uyyamadilu in your own words.
      </p>

      <section className="rounded-xl border border-border p-5 bg-bg-primary mb-8">
        <h2 className="font-heading text-h3 text-charcoal mb-3">Add a testimony</h2>
        {error !== null ? <Alert tone="error">{error}</Alert> : null}
        {success !== null ? <Alert tone="success">{success}</Alert> : null}
        <Textarea
          value={body}
          rows={5}
          maxLength={BODY_MAX}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Tell us what stood out — quality, comfort, service, anything."
          className="mt-3"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="font-body text-body-xs text-text-secondary">
            {trimmed.length} / {BODY_MAX} characters {trimmed.length < BODY_MIN ? `(min ${BODY_MIN})` : ''}
          </span>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </section>

      <h2 className="font-heading text-h2 text-charcoal mb-4">Your testimonies</h2>

      {vm.isLoading && vm.testimonials.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : vm.error !== null ? (
        <Alert tone="error">{vm.error}</Alert>
      ) : vm.testimonials.length === 0 ? (
        <p className="font-body text-body text-text-secondary">
          No testimonies yet — yours will land here once you submit.
        </p>
      ) : (
        <>
          <Stagger as="ul" className="space-y-3" amount={0.05}>
            {vm.testimonials.map((t) => (
              <StaggerItem as="li" key={t.id}>
                <div className="block rounded-xl border border-border p-5 bg-bg-primary">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-body text-body text-charcoal whitespace-pre-wrap flex-1">
                      {t.body}
                    </p>
                    <button
                      type="button"
                      className="text-body-xs underline underline-offset-2 text-rust hover:text-charcoal disabled:opacity-50"
                      disabled={pendingDeleteId === t.id}
                      onClick={() => void handleDelete(t.id)}
                    >
                      {pendingDeleteId === t.id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                  <p className="font-body text-body-xs text-text-secondary mt-2">
                    Submitted {formatDate(t.created_at)}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="mt-6">
            <Paginator
              page={vm.page}
              pageSize={vm.pageSize}
              total={vm.total}
              onPageChange={vm.setPage}
              disabled={vm.isLoading}
            />
          </div>
        </>
      )}
    </>
  )
}
